import offlineStorage from './OfflineStorage'
import { vendaService, checkApiStatus } from './api'

class SyncService {
  constructor() {
    this.isSyncing = false
    this.listeners = []
    
    // Configurar listeners de conexão
    this.setupConnectionListeners()
  }

  setupConnectionListeners() {
    window.addEventListener('online', () => {
      console.log('Conexão restaurada! Iniciando sincronização...')
      this.notifyListeners('online')
      this.syncPendingData()
    })
    
    window.addEventListener('offline', () => {
      console.log('Conexão perdida. Modo offline ativado.')
      this.notifyListeners('offline')
    })
  }

  // Registrar listener para eventos de sync
  onStatusChange(callback) {
    this.listeners.push(callback)
    
    // Retornar função para remover listener
    return () => {
      const index = this.listeners.indexOf(callback)
      if (index > -1) {
        this.listeners.splice(index, 1)
      }
    }
  }

  notifyListeners(status) {
    this.listeners.forEach(callback => {
      try {
        callback(status)
      } catch (error) {
        console.error('Erro ao notificar listener:', error)
      }
    })
  }

  // Verificar se está online
  isOnline() {
    return navigator.onLine
  }

  // Verificar se API está acessível
  async isApiAvailable() {
    return await checkApiStatus()
  }

  // Sincronizar dados pendentes
  async syncPendingData() {
    if (this.isSyncing) {
      console.log('Sincronização já em andamento...')
      return { success: false, message: 'Sincronização já em andamento' }
    }

    if (!this.isOnline()) {
      return { success: false, message: 'Sem conexão com a internet' }
    }

    const apiAvailable = await this.isApiAvailable()
    if (!apiAvailable) {
      return { success: false, message: 'API não disponível' }
    }

    this.isSyncing = true
    this.notifyListeners('syncing')

    try {
      const result = await this.syncVendas()
      
      // Limpar cache antigo periodicamente
      await offlineStorage.limparVendasSincronizadas()
      
      this.notifyListeners('synced')
      return result
      
    } catch (error) {
      console.error('Erro na sincronização:', error)
      this.notifyListeners('error')
      return { 
        success: false, 
        message: 'Erro durante sincronização',
        error: error.message 
      }
    } finally {
      this.isSyncing = false
    }
  }

  // Sincronizar vendas pendentes
  async syncVendas() {
    const vendasPendentes = await offlineStorage.listarVendasPendentes()
    
    if (vendasPendentes.length === 0) {
      return { success: true, sincronizadas: 0, message: 'Nenhuma venda pendente' }
    }

    console.log(`Sincronizando ${vendasPendentes.length} vendas...`)

    // Preparar dados para API
    const vendasParaSync = vendasPendentes.map(venda => ({
      sync_id: venda.syncId,
      produto_id: venda.produto_id,
      cliente_id: venda.cliente_id,
      quantidade: venda.quantidade,
      valor_unitario: venda.valor_unitario,
      forma_pagamento: venda.forma_pagamento,
      observacoes: venda.observacoes,
      created_offline: true
    }))

    try {
      const response = await vendaService.sincronizar(vendasParaSync)
      const resultado = response.data.data

      // Marcar vendas sincronizadas
      for (const venda of vendasPendentes) {
        const erro = resultado.erros?.find(e => e.sync_id === venda.syncId)
        
        if (erro) {
          // Houve erro na sincronização desta venda
          await offlineStorage.incrementarTentativa(venda.syncId)
          console.error(`Erro ao sincronizar venda ${venda.syncId}:`, erro.erro)
        } else {
          // Venda sincronizada com sucesso
          await offlineStorage.marcarVendaSyncronizada(venda.syncId)
        }
      }

      // Recalcular vendas pendentes
      const restantes = await offlineStorage.contarVendasPendentes()

      return {
        success: true,
        sincronizadas: resultado.sincronizadas,
        erros: resultado.erros?.length || 0,
        pendentes: restantes,
        message: `${resultado.sincronizadas} vendas sincronizadas. ${restantes} pendentes.`
      }

    } catch (error) {
      console.error('Erro ao sincronizar com API:', error)
      
      // Incrementar tentativas para todas as vendas pendentes
      for (const venda of vendasPendentes) {
        await offlineStorage.incrementarTentativa(venda.syncId)
      }

      throw error
    }
  }

  // Forçar sincronização
  async forceSync() {
    return await this.syncPendingData()
  }

  // Obter status atual
  async getStatus() {
    const storageInfo = await offlineStorage.getStorageInfo()
    
    return {
      online: this.isOnline(),
      syncing: this.isSyncing,
      apiAvailable: await this.isApiAvailable(),
      ...storageInfo
    }
  }

  // Cache de clientes e produtos
  async atualizarCache(clientes = null, produtos = null) {
    if (clientes) {
      await offlineStorage.salvarClientesCache(clientes)
    }
    if (produtos) {
      await offlineStorage.salvarProdutosCache(produtos)
    }
  }

  // Buscar clientes (cache ou online)
  async buscarClientes(termo = '') {
    if (this.isOnline()) {
      try {
        // Tentar buscar online primeiro
        const { data } = await import('./api').then(m => m.clienteService.buscarRapido(termo))
        // Atualizar cache
        await offlineStorage.salvarClientesCache(data.data)
        return data.data
      } catch (error) {
        // Fallback para cache
        console.log('Buscando clientes do cache...')
        return await offlineStorage.buscarClientesCachePorNome(termo)
      }
    } else {
      // Modo offline - usar cache
      return await offlineStorage.buscarClientesCachePorNome(termo)
    }
  }

  // Buscar produtos (cache ou online)
  async buscarProdutos(termo = '') {
    if (this.isOnline()) {
      try {
        // Tentar buscar online primeiro
        const { data } = await import('./api').then(m => m.produtoService.buscarRapido(termo))
        // Atualizar cache
        await offlineStorage.salvarProdutosCache(data.data)
        return data.data
      } catch (error) {
        // Fallback para cache
        console.log('Buscando produtos do cache...')
        return await offlineStorage.buscarProdutosCachePorNome(termo)
      }
    } else {
      // Modo offline - usar cache
      return await offlineStorage.buscarProdutosCachePorNome(termo)
    }
  }
}

export default new SyncService()
